import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { normalizeSkills } from "@/utils/skillNormalization";

export interface SkillFacet {
	name: string;
	count: number | null;
}

// Skill facet list. Primary source: org_skills (normalized skills linked to the
// organization's candidates, most linked first). Fallback: org-scoped scan of
// candidates.skills normalized client-side.
export function useSkillFacets() {
	return useQuery({
		queryKey: ['people-search-skill-facets'],
		queryFn: async (): Promise<SkillFacet[]> => {
			const { data: { user } } = await supabase.auth.getUser();
			if (!user) throw new Error('User not authenticated');
			const { data: userOrgs } = await supabase
				.from('user_organization')
				.select('organization_uuid')
				.eq('user_uuid', user.id)
				.single();
			if (!userOrgs) throw new Error('User not associated with any organization');

			// @ts-ignore - table exists but types not yet regenerated
			const { data: orgSkills, error: orgSkillsError } = await supabase
				.from('org_skills')
				.select('name')
				.eq('organization_uuid', userOrgs.organization_uuid)
				.gt('candidate_link_count', 0)
				.order('candidate_link_count', { ascending: false })
				.order('name', { ascending: true })
				.limit(500);

			// candidate_link_count counts link rows, not candidates, so it is not shown
			if (!orgSkillsError && orgSkills && orgSkills.length > 0)
				// @ts-ignore - untyped rows from stale generated types
				return orgSkills.map(row => ({ name: row.name, count: null }));

			const { data: rows, error } = await supabase
				.from('candidates')
				.select('skills')
				.eq('organization_uuid', userOrgs.organization_uuid)
				.not('skills', 'is', null);
			if (error) throw error;

			const counts = new Map<string, number>();
			for (const row of rows || [])
				for (const skill of normalizeSkills(row.skills || []))
					counts.set(skill, (counts.get(skill) || 0) + 1);

			return Array.from(counts.entries())
				.map(([name, count]) => ({ name, count }))
				.sort((a, b) => (b.count || 0) - (a.count || 0))
				.slice(0, 500);
		},
		staleTime: 5 * 60 * 1000,
	});
}
